import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface IntroAnimationProps {
  onComplete: () => void;
}

export function IntroAnimation({ onComplete }: IntroAnimationProps) {
  const [stage, setStage] = useState<"logo" | "text">("logo");
  const [lettersRevealed, setLettersRevealed] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  const letters = ["R", "a", "y", "l", "o", "o", "p"];

  useEffect(() => {
    // Transition from logo-only to logo+text
    const timer = setTimeout(() => {
      setStage("text");
    }, 1100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (stage === "text") {
      const interval = setInterval(() => {
        setLettersRevealed((prev) => {
          if (prev < letters.length) {
            return prev + 1;
          }
          clearInterval(interval);
          return prev;
        });
      }, 40); // Reveal next letter every 0.1s

      return () => clearInterval(interval);
    }
  }, [stage, letters.length]);

  useEffect(() => {
    if (lettersRevealed === letters.length) {
      // Allow time for final letters to animate in and then hold briefly
      const exitTimer = setTimeout(() => {
        setIsExiting(true);
      }, 750); // 1.5s after the last letter starts

      const finishTimer = setTimeout(() => {
        onComplete();
      }, 1500); // 0.5s after fade out starts

      return () => {
        clearTimeout(exitTimer);
        clearTimeout(finishTimer);
      };
    }
  }, [lettersRevealed, letters.length, onComplete]);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-background"
        initial={{ opacity: 1 }}
        animate={{ opacity: isExiting ? 0 : 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-center">
          <motion.div
            layout
            initial={{ scale: 3.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              scale: { duration: 1.2, ease: "easeInOut" },
              opacity: { duration: 1.2, ease: "easeInOut" },
              layout: { duration: 0.4, ease: "easeOut" },
            }}
            className="shrink-0 z-10"
          >
            <img
              src="/src/assets/png/logo.png"
              alt="Rayloop Logo"
              className="h-28 w-28 object-contain dark:invert"
            />
          </motion.div>

          {stage === "text" && (
            <div className="ml-2 flex text-8xl font-bold tracking-tight text-foreground z-20">
              {letters.slice(0, lettersRevealed).map((letter, index) => (
                <motion.span
                  layout
                  key={`${letter}-${index}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    opacity: { duration: 0.5 },
                    x: { duration: 0.5, ease: "easeOut" },
                    layout: { duration: 0.4, ease: "easeOut" },
                  }}
                  className="inline-block"
                >
                  {letter}
                </motion.span>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
